// areaOptions.ts — the shared vocabulary + label/search helpers for the
// "preferred areas" picker used by the signup PreferencesScreen, the
// in-workspace PrefsEditModal, and the EditWorkspaceModal.
//
// Why centralize (beyond prefs-options.ts):
//   - The picker now spans coarse regions, a CBD shortcut, AND the 28 URA
//     districts (labelled by their towns). All three editors must offer the
//     exact same set, and every token they can emit MUST be resolvable by
//     the backend's AreasToDistricts — an unknown token is silently dropped
//     and blanks the seeded feed (the "CBD free-text incident").
//   - Districts are stored as tokens ("d18") but always DISPLAYED as their
//     human-readable town label ("Tampines / Pasir Ris") — never "D18".
//     Keeping one label resolver here guarantees the edit UI, the overview
//     row, and the prefs banner all agree.

import { DISTRICT_OPTIONS, districtToken, parseDistrictToken } from "./districts";

// Loosely typed translate fn — matches the workspace-scoped `useWsT()`
// wrapper (TFn) exactly. react-i18next's own `t` isn't structurally
// assignable to this under the project's strict config, so callers that
// hold the raw `t` pass it as `t as Translate` (the same idiom the
// WsTCtx provider uses with `t as TFn`).
export type Translate = (key: string, opts?: Record<string, unknown>) => string;

// Macro tokens resolved via i18n auth.prefs.areaOpts.*
export const REGION_TOKENS = ["central", "east", "west", "north", "northeast"] as const;
export const CBD_TOKEN = "cbd";
export const ANY_TOKEN = "any";

// Always-visible toggle chips, in display order: the coarse regions, the
// CBD shortcut, then "Anywhere" (which is mutually exclusive with the rest).
export const QUICK_PICK_TOKENS: readonly string[] = [...REGION_TOKENS, CBD_TOKEN, ANY_TOKEN];

export interface AreaOption {
  token: string;                    // "central" | "cbd" | "d18"
  label: string;                    // human-readable, resolved
  group: "region" | "district";
  search: string;                   // lowercased haystack for the filter box
}

// areaLabel resolves any stored token to the human-readable label shown to
// the user. District tokens become their town label (no "D18" codes);
// macro tokens fall back through i18n.
export function areaLabel(token: string, t: Translate): string {
  const code = parseDistrictToken(token);
  if (code) {
    const hit = DISTRICT_OPTIONS.find(([c]) => c === code);
    return hit ? hit[1] : token;
  }
  return t(`auth.prefs.areaOpts.${token}`, { defaultValue: token });
}

// searchableAreaOptions returns everything offered in the search dropdown:
// the macro areas (regions + CBD) and all 28 districts. "Anywhere" is
// intentionally excluded — it's a quick-pick toggle, not something you
// search for. District search haystacks include the numeric code ("18",
// "d18") so typing a number still finds the town.
export function searchableAreaOptions(t: Translate): AreaOption[] {
  const macro: AreaOption[] = [...REGION_TOKENS, CBD_TOKEN].map((tok) => {
    const label = t(`auth.prefs.areaOpts.${tok}`, { defaultValue: tok });
    return { token: tok, label, group: "region" as const, search: `${tok} ${label.toLowerCase()}` };
  });
  const districts: AreaOption[] = DISTRICT_OPTIONS.map(([code, label]) => ({
    token: districtToken(code),
    label,
    group: "district" as const,
    search: `${code} d${code} district ${code} ${label.toLowerCase()}`,
  }));
  return [...macro, ...districts];
}

// isDistrictToken — convenience for splitting selected tokens into the
// always-visible quick-pick set vs. the searchable district chips.
export function isDistrictToken(token: string): boolean {
  return parseDistrictToken(token) !== "";
}
